// ─────────────────────────────────────────────────────────────
// LukaDuka - App Router (ver14)
// The ONLY file that imports from multiple modules.
//
// Route logic:
//   ?demo=true                 → DemoApp (Sarah Mitchell)
//   ?reset=true  OR  recovery  → ResetPassword (isolated recovery page)
//   ?pin-demo=true (logged in) → PinDemo harness (TEMPORARY - see below)
//   Not logged in              → AuthScreen (login / sign up)
//   Logged in, incomplete      → OnboardingFlow (wrapped in UserProvider)
//   Logged in, complete        → AppShell (all four pillars)
//
// Changes from ver13 (2 November 2026, Offline Capture screen - Layer 2c):
//   - Start-up now decides "offline or not" BEFORE any normal start-up call.
//     Verified 2 Nov 2026: opened offline, the old start-up waited 25-31s
//     on the server and then showed the error screen (valid login) or the
//     sign-in screen (login more than ~1 hour old). Design brief:
//     lukaduka-offline-capture-screen-design-ver4.md, section 4.1.
//
//     Order (only things that answer in seconds):
//       1. Read the stored login from the phone (offlineIdentity) - no
//          server call.
//       2. Phone reports offline                  -> offline state.
//       3. Quick reachability check (3s limit) fails -> offline state.
//       4. Otherwise normal start-up exactly as before, with a 12s safety
//          limit. If the limit is reached and a stored login exists ->
//          offline state (a late result is then ignored, so it can never
//          replace the offline screen mid-typing).
//     Offline state = 'offline-capture' (stored login) or
//     'offline-signed-out' (no stored login - Decision I).
//
//   - While on 'offline-capture', SIGNED_IN and SIGNED_OUT announcements are
//     ignored. Leaving the offline screen only ever happens through
//     checkSession (the "Open LukaDuka" / "Try again" button) - Decision H.
//   - The launch splash is dismissed for both offline states.
//   - resolveAppStateForUser takes an optional isStale() so a late result
//     from a timed-out start-up is dropped.
//   - Unchanged: P20 birth-record protection - nothing offline or failed
//     can ever route to 'onboarding'.
//   - Layer 2 shows interim screens (OfflineCaptureScreen ver0); the real
//     capture screen arrives in Layer 3. Do NOT release to production
//     before Layer 3.
//
// Changes from ver12 (29 October 2026, Offline capture sync fix):
//   - FIX: the app swapped to ProfileLoadErrorScreen ("We couldn't open
//     your story just now") whenever it came back on screen while offline,
//     throwing away anything typed but not saved (Quick Capture text, an
//     open Enrich screen).
//
//     Root cause: @supabase/auth-js emits SIGNED_IN on every hidden ->
//     visible change (_recoverAndRefresh), even though nobody signed in.
//     Since April this listener treated every SIGNED_IN as a fresh sign-in
//     and reloaded the profile. Online that was invisible (and a wasted
//     server call); offline the reload failed and replaced the whole app.
//
//     Fix (Option A, approved 29 Oct 2026): ignore SIGNED_IN when Router is
//     already showing the app OR onboarding for the SAME user. It still
//     reacts to a real sign-in (from 'auth'), a different user, the
//     'loading' state, and the 'profile-error' state (so returning to the
//     app from the error screen still retries). Onboarding is included in
//     "ignore" so a swap can never throw away a half-entered date of birth.
//
//     Unchanged: the P20 birth-record protection. A failed profile load
//     still goes to 'profile-error', never to 'onboarding'.
//
//     Implementation: appState and userId are mirrored into refs because
//     the auth listener is registered once and would otherwise see only
//     their initial values.
//
// Changes from ver11:
//   - FIX: the launch splash never went away. index.html and AppShell were
//     updated for the sibling-splash architecture but this file was not, so
//     on every path that does NOT reach AppShell - which includes the
//     logged-out sign-in screen - nothing ever called dismissBootSplash().
//     The app rendered in about 130ms and then sat behind an opaque green
//     screen indefinitely. Measured live: splash still at full opacity,
//     with no is-dismissed class, 27 seconds after load.
//
//     Two things landed here:
//
//       1. dismissBootSplash() is now called once Router settles on a view
//          that renders its own UI. The 'app' branch is deliberately absent
//          - AppShell dismisses it itself, after the user profile has
//          loaded, so the splash covers the profile fetch rather than an
//          empty Timeline filling in behind it.
//
//       2. AppLoadingScreen renders nothing. The splash is a sibling of
//          #root and sits on top at z-index 9999, so anything rendered
//          underneath during launch is invisible anyway; the body
//          background is the same brand green, so null cannot flash.
//          It previously rendered <LukaLoadingScreen />, which was the
//          earlier fix for a cream flash that the sibling splash has now
//          made redundant.
//
//     WHY THE SAFETY NET DID NOT SAVE US: bootSplash.ts arms a 10-second
//     hard timeout when the module is imported. Before this change its only
//     importer was AppShell, which is lazy-loaded - so on the logged-out
//     path the module never loaded and the timeout was never armed. Router
//     is eagerly loaded, so importing it here arms the safety net on every
//     route. Keep at least one EAGER importer of bootSplash.ts.
//
// Changes from ver10:
//   - FIX: password-reset landing was unreachable. The recovery flow relied
//     on ?reset=true surviving in the URL, but Supabase strips the query
//     string when it consumes the {{ .ConfirmationURL }} link and redirects
//     to the bare Site URL. With the flag gone, Router saw a normal logged-in
//     user and swept the recovering user into onboarding / the app instead of
//     the set-new-password form. (Confirmed live: post-click address bar read
//     only "staging.lukaduka.life/#".)
//
//     The fix stops depending on the strippable flag and instead detects the
//     recovery session two independent ways, BOTH of which win over the
//     session routing below and BOTH of which fail *towards* showing the
//     reset form (the safe direction):
//
//       1. isRecoveryHash — read synchronously at module load from the URL
//          fragment. When Supabase redirects after a recovery link, it places
//          the session in the hash with "type=recovery". That marker is put
//          there by Supabase and arrives with the session, so it cannot be
//          stripped the way our ?reset=true query was. Synchronous, so there
//          is no flash and no race with the session check.
//
//       2. isRecovery (sticky state) — set when the PASSWORD_RECOVERY auth
//          event fires. Backstop for the rare case where the client processed
//          and cleaned the hash before this module read it (warm client, some
//          client versions). It can only ever turn ON, so it is fail-safe.
//
//     ?reset=true is kept as a still-valid third entry (belt and braces) in
//     case a future link preserves it. AuthProvider and ResetPassword are
//     unchanged — Router is the routing authority for this flow.
//
//     Known residual edge (documented, not fixed here): a FULL browser reload
//     while on the reset form — after the client has cleaned the hash — loses
//     both signals, so the user lands in the app on their live recovery
//     session rather than back on the form. Rare, non-breaking. A proper fix
//     means tracking "password not yet reset" as account state — later work.
//
// Changes from ver9:
//   - ADD: ?reset=true renders the isolated ResetPassword page (the
//     redirect target of the forgot-password email link). It is placed
//     BEFORE any session-based routing, exactly like ?demo, so the
//     temporary recovery session - which looks like a normal login to
//     this file's checkSession/onAuthStateChange logic - cannot sweep the
//     recovering user into AppShell before they set a new password.
//     Exact-match flag via URLSearchParams, same as the other flags.
//
// Changes from ver8:
//   - FIX: ?pin-demo=true was ALSO matching the demo check, because
//     "pin-demo=true".includes("demo=true") is true - so DemoApp rendered
//     first and the PIN harness was never reached. Both flags now use
//     URLSearchParams for an EXACT parameter match, which removes the
//     substring collision entirely.
//
// Changes from ver7:
//   - TEMPORARY: ?pin-demo=true renders the PinDemo harness in place of
//     AppShell for a LOGGED-IN user, so the PIN module (migration v61) can
//     be tested against a real authenticated session (the RPCs need
//     auth.uid()). Remove the PinDemo lazy import, the isPinDemo flag, and
//     the ternary in the 'app' branch once PIN testing is done.
//
// Changes from ver6:
//   - FIX: auth-lock deadlock. The SIGNED_IN branch of onAuthStateChange
//     called getProfile() (a Supabase query) directly inside the callback,
//     which re-enters Supabase's auth lock while it is held and deadlocks
//     the client (queries hang with no request sent until a hard refresh).
//     The profile fetch + state update are now deferred with setTimeout(…, 0)
//     so the callback returns and releases the lock first.
//
// Changes from ver5:
//   - Fixed: supabase imported from shared service (not onboardingService)
//   - Fixed: OnboardingFlow wrapped in UserProvider (it calls useUser())
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react'
import AuthScreen         from '@/modules/auth/pages/AuthScreen'
import { UserProvider }   from '@/app/providers/UserProvider'
import { ProfileLoadErrorScreen } from '@/app/components/ProfileLoadErrorScreen'

// Heavy, mutually-exclusive top-level views are lazy-loaded so a cold
// open only downloads the state it actually needs. AuthScreen stays a
// static import so the login screen (first thing a logged-out visitor
// sees) appears instantly with no extra fetch.
const OnboardingFlow = lazy(() =>
  import('@/modules/onboarding').then((m) => ({ default: m.OnboardingFlow }))
)
const AppShell = lazy(() =>
  import('@/app/AppShell').then((m) => ({ default: m.AppShell }))
)
const DemoApp = lazy(() => import('@/app/DemoApp'))

// Isolated recovery-password page. Reached via ?reset=true OR a detected
// recovery session (see the recovery flags below) - the redirect target of
// the forgot-password email link. Lazy-loaded because it is rarely hit,
// mirroring DemoApp.
const ResetPassword = lazy(() => import('@/modules/auth/pages/ResetPassword'))

// TEMPORARY: PIN test harness. Remove with the ?pin-demo branch below.
const PinDemo = lazy(() => import('@/modules/security/PinDemo'))

// ── IMPORTANT ────────────────────────────────────────────────
// Always import supabase from the shared service — never from
// a module's own service file. One client instance, app-wide.
import { supabase }          from '@/shared/services/supabase'
import { loadProfileWithRetry } from '@/modules/onboarding/services/onboardingService'
import { refreshLocationPreferenceFromProfile } from '@/shared/services/locationPreference'
// Router is EAGERLY loaded, so this import is also what arms the 10-second
// safety net inside bootSplash.ts on every route. Do not make this the only
// importer a lazy chunk provides - see the ver12 note above.
import { dismissBootSplash } from '@/shared/utils/bootSplash'
// ver14: offline start-up. Eager imports on purpose - small, and must be
// available the moment the app opens with no connection.
import {
  getStoredUserId,
  isServerReachable,
  OfflineCaptureScreen,
  OfflineSignedOutScreen,
} from '@/app/offline'

// ver14: start-up time limits (design brief section 4.1)
const QUICK_CHECK_MS    = 3_000
const STARTUP_LIMIT_MS  = 12_000

type AppState =
  | 'loading'
  | 'auth'
  | 'onboarding'
  | 'app'
  | 'profile-error'
  | 'offline-capture'      // ver14: no connection, stored login found
  | 'offline-signed-out'   // ver14: no connection, no stored login

// Exact-match URL flags. Using URLSearchParams (not string.includes) so
// that 'pin-demo' can never be mistaken for 'demo' - the two are distinct
// parameters, matched by name.
const params    = new URLSearchParams(window.location.search)
const isDemo    = params.get('demo') === 'true'
const isReset   = params.get('reset') === 'true'   // forgot-password recovery (flag path)
// TEMPORARY: PIN harness. Development builds only - in a production build
// import.meta.env.DEV is false, so ?pin-demo=true falls through to the app.
const isPinDemo = import.meta.env.DEV && params.get('pin-demo') === 'true'

// Recovery detection that does NOT depend on the strippable ?reset=true
// flag. When Supabase redirects after consuming a recovery link, it puts
// the session in the URL fragment with "type=recovery". Read it here,
// synchronously at module load, BEFORE any routing runs - so a recovering
// user is held on the reset form with no flash and no race. This marker
// arrives with the session and is not stripped the way our query flag was.
const isRecoveryHash = window.location.hash.includes('type=recovery')

// Launch is covered by the static splash in index.html, which is a sibling
// of #root at z-index 9999 and survives React mounting. So there is nothing
// to render here: anything underneath is hidden by the splash anyway, and
// the body background is the same brand green, so null cannot flash.
//
// History: this once painted its own cream (#FAF7F2) panel, which appeared
// as a pale flash mid-launch. That was replaced by <LukaLoadingScreen />,
// which the sibling splash has now made redundant in turn.
function AppLoadingScreen() {
  return null
}

export function Router() {
  const [appState, setAppState] = useState<AppState>('loading')
  const [userId,   setUserId]   = useState<string | null>(null)
  // Set when the user taps "Show me Settings first" on the onboarding
  // close screen. Read once by AppShell on mount.
  const [openSettingsOnEntry, setOpenSettingsOnEntry] = useState(false)
  // Sticky backstop for recovery detection. Set true if the
  // PASSWORD_RECOVERY auth event fires (covers the case where the client
  // cleaned the URL hash before the module-load read above could see it).
  // Only ever turns ON - fail-safe towards showing the reset form.
  const [isRecovery, setIsRecovery] = useState(false)
  // ver14: the account holder's ID read from the phone for the offline
  // screen. Never used to authorise anything - the server checks the real
  // login when held captures are sent.
  const [offlineUserId, setOfflineUserId] = useState<string | null>(null)

  // ver13: live mirrors of appState / userId for the auth listener, which is
  // registered once and would otherwise only ever see the initial values.
  const appStateRef = useRef<AppState>(appState)
  const userIdRef   = useRef<string | null>(userId)
  useEffect(() => {
    appStateRef.current = appState
    userIdRef.current   = userId
  }, [appState, userId])

  // Resolves app state for an authenticated user id: load the profile
  // (with retry), then route on the result. 'failed' NEVER routes to
  // 'onboarding' - see onboardingService.getProfile (Bug P20). Shared by
  // the initial session check, the SIGNED_IN listener, and the "Try
  // again" button on ProfileLoadErrorScreen, so all three retry the
  // exact same way.
  const resolveAppStateForUser = useCallback(async (uid: string, isStale?: () => boolean) => {
    const result = await loadProfileWithRetry(uid)
    // Location switch (3 Nov 2026, location-privacy ver5): the profile just
    // loaded from the database (select '*', so privacy_preferences is in it),
    // so refresh the phone copy that Quick Capture and the Offline Capture
    // screen read. Only an exact storeGpsCoordinates true means on. Done
    // before the stale check: a late result is still the confirmed value.
    if (result.status === 'loaded') {
      refreshLocationPreferenceFromProfile(uid, result.profile.privacy_preferences)
    }
    // ver14: a start-up that already timed out to the offline screen must
    // never replace it with a late result.
    if (isStale?.()) return
    setUserId(uid)
    if (result.status === 'loaded') {
      setAppState(result.profile.onboarding_complete ? 'app' : 'onboarding')
    } else {
      console.error('Router: profile load failed:', result.code)
      setAppState('profile-error')
    }
  }, [])

  // ── Initial session check ──────────────────────────────
  // ver14: decides "offline or not" first, using only things that answer
  // in seconds, then runs the original start-up with a safety limit.
  const checkSession = useCallback(async () => {
    const storedUserId = getStoredUserId()

    const goOffline = () => {
      if (storedUserId) {
        setOfflineUserId(storedUserId)
        setAppState('offline-capture')
      } else {
        setAppState('offline-signed-out')
      }
    }

    const phoneOffline = typeof navigator !== 'undefined' && navigator.onLine === false
    if (phoneOffline || !(await isServerReachable(QUICK_CHECK_MS))) {
      goOffline()
      return
    }

    // Safety limit. Only meaningful with a stored login - without one there
    // is no token to renew, so getSession answers quickly anyway.
    let timedOut = false
    const limit = window.setTimeout(() => {
      if (!storedUserId) return
      timedOut = true
      console.warn('Router: start-up limit reached - opening offline screen')
      goOffline()
    }, STARTUP_LIMIT_MS)
    const isStale = () => timedOut

    try {
      const { data } = await supabase.auth.getSession()
      if (isStale()) return
      const user = data.session?.user

      if (!user) {
        setAppState('auth')
        return
      }

      await resolveAppStateForUser(user.id, isStale)
    } catch (error) {
      if (isStale()) return
      console.error('Router: checkSession error:', error)
      setAppState('auth')
    } finally {
      window.clearTimeout(limit)
    }
  }, [resolveAppStateForUser])

  useEffect(() => {
    checkSession()

    // ── Listen for auth changes ────────────────────────────
    // The callback runs while Supabase holds its internal auth lock.
    // Any Supabase call made directly inside it (getProfile -> a query)
    // re-enters that lock and deadlocks the client - queries then hang
    // with no request sent until a hard refresh. So the SIGNED_IN work
    // is deferred with setTimeout(…, 0): the callback returns and frees
    // the lock first, then the profile fetch runs safely.
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      // Recovery backstop. If the client surfaces the recovery as
      // PASSWORD_RECOVERY, latch it so the render guard below holds the
      // user on the reset form even if the hash was already cleaned.
      // Return early: a recovery session must NEVER fall through to the
      // profile fetch / onboarding routing.
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true)
        return
      }

      // ver14: on the offline screen, ignore sign-in / sign-out
      // announcements entirely. Leaving it only happens through
      // checkSession ("Open LukaDuka" / "Try again") - Decision H - so the
      // screen can never be pulled away mid-typing. Nothing is sent while
      // offline, and the server checks the real login when it is.
      if (appStateRef.current === 'offline-capture' &&
          (event === 'SIGNED_IN' || event === 'SIGNED_OUT')) {
        return
      }

      if (event === 'SIGNED_OUT') {
        setUserId(null)
        setAppState('auth')
        return
      }

      if (event === 'SIGNED_IN' && session?.user) {
        const user = session.user

        // ver13: auth-js re-announces SIGNED_IN every time the app comes
        // back on screen. If we are already showing the app or onboarding
        // for this same person, nothing has changed - do NOT reload the
        // profile (offline that reload fails and replaces the whole app,
        // losing unsaved work). Real sign-ins, a different user, 'loading'
        // and 'profile-error' still fall through to the reload below.
        const alreadyShowingThisUser =
          userIdRef.current === user.id &&
          (appStateRef.current === 'app' || appStateRef.current === 'onboarding')
        if (alreadyShowingThisUser) return

        setTimeout(async () => {
          try {
            await resolveAppStateForUser(user.id)
          } catch (error) {
            console.error('Router: onAuthStateChange profile fetch error:', error)
          }
        }, 0)
      }
    })

    return () => listener.subscription.unsubscribe()
  }, [checkSession, resolveAppStateForUser])

  // ── Dismiss the launch splash (ver12) ──────────────────────
  // Take the splash down once Router has settled on a view that renders
  // its own UI. The 'app' branch is deliberately absent - AppShell
  // dismisses it itself, after the user profile has loaded, so the splash
  // covers the profile fetch rather than an empty Timeline filling in
  // behind it. dismissBootSplash() is idempotent and safe to call twice.
  useEffect(() => {
    if (isDemo || isReset || isRecoveryHash || isRecovery) {
      dismissBootSplash()
      return
    }
    if (appState === 'auth' || appState === 'onboarding' || appState === 'profile-error' ||
        appState === 'offline-capture' || appState === 'offline-signed-out') {
      dismissBootSplash()
    }
  }, [appState, isRecovery])

  // ── Demo ───────────────────────────────────────────────────
  if (isDemo) {
    return (
      <Suspense fallback={<AppLoadingScreen />}>
        <DemoApp />
      </Suspense>
    )
  }

  // ── Password reset (isolated recovery route) ───────────────
  // Rendered whenever the URL carries ?reset=true, OR a recovery session is
  // detected via the hash marker (module load) or the PASSWORD_RECOVERY
  // event (sticky). Placed here, before any session routing below, so the
  // temporary recovery session (which looks like a normal login to
  // checkSession/onAuthStateChange above) cannot sweep the user into AppShell
  // before they set a new password. The effect above still runs, but this
  // render-time guard wins on every render, exactly as ?demo does.
  if (isReset || isRecoveryHash || isRecovery) {
    return (
      <Suspense fallback={<AppLoadingScreen />}>
        <ResetPassword />
      </Suspense>
    )
  }

  // ── Offline at start-up (ver14) ────────────────────────────
  // Never routes to onboarding. Leaving these screens re-runs checkSession.
  if (appState === 'offline-capture' && offlineUserId) {
    return <OfflineCaptureScreen userId={offlineUserId} onOpenApp={checkSession} />
  }
  if (appState === 'offline-signed-out') {
    return <OfflineSignedOutScreen onTryAgain={checkSession} />
  }

  // ── Loading ────────────────────────────────────────────────
  if (appState === 'loading') {
    return <AppLoadingScreen />
  }

  // ── Not logged in → Auth screen ────────────────────────────
  if (appState === 'auth') {
    return <AuthScreen />
  }

  // ── Logged in, profile failed to load after retries (P20) ──
  // Never falls through to onboarding - see resolveAppStateForUser.
  // "Try again" re-runs the exact same session + profile load.
  if (appState === 'profile-error') {
    return <ProfileLoadErrorScreen onRetry={checkSession} />
  }

  // ── Logged in, onboarding incomplete ──────────────────────
  // Wrapped in UserProvider because OnboardingFlow calls useUser()
  if (appState === 'onboarding' && userId) {
    return (
      <UserProvider userId={userId}>
        <Suspense fallback={<AppLoadingScreen />}>
          <OnboardingFlow
            onComplete={(uid?: string) => {
              if (uid) setUserId(uid)
              setAppState('app')
            }}
            onOpenSettings={(uid?: string) => {
              if (uid) setUserId(uid)
              setOpenSettingsOnEntry(true)
              setAppState('app')
            }}
          />
        </Suspense>
      </UserProvider>
    )
  }

  // ── Logged in, onboarding complete → Main app ──────────────
  // TEMPORARY: ?pin-demo=true shows the PIN harness instead of AppShell.
  if (appState === 'app' && userId) {
    return (
      <Suspense fallback={<AppLoadingScreen />}>
        {isPinDemo ? <PinDemo /> : (
          <AppShell userId={userId} openSettingsOnMount={openSettingsOnEntry} />
        )}
      </Suspense>
    )
  }

  // Fallback — should never reach here
  return null
}
